import React, { useState, useEffect } from "react";
import { Row, Col, Form as BTForm } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTheme } from "styled-components";
import { useNavigation } from "utils/navigate";
import {
  SignupFlowFooter,
  SignupFlowContent,
  SignupFlowWrapperContainer,
  FooterRight,
} from "../../Components/SignupFlowWrapper.styles";
import { Button } from "components/Button";
import { Form, Select, DateTimePicker } from "components/Form";
import { Loader } from "components/Loader";
import {
  PersonalDetailsContainer,
  FormSection,
  StyledInput,
  PersonalDetailsSelectStyle,
  FormRow,
  Divider,
} from "./PersonalDetails.styles";
import { Text } from "style/v2/components/Typography";
import {
  getStates,
  combinedPersonalDetails,
} from "features/SurmountAccount/service";
import { useDispatch } from "react-redux";
import { setCreateAccountData } from "features/SurmountAccount/slice/index";
import { useMutation } from "hooks/useMutation";

// SSN formatting function from CountryCitizenship
const formatSSN = (value, previousValue = "") => {
  if (!value) return value;
  const currentValue = value.replace(/[^\d]/g, "");
  const cvLength = currentValue.length;

  if (!previousValue || value.length > previousValue.length) {
    if (cvLength < 4) return currentValue;
    if (cvLength < 6)
      return `${currentValue.slice(0, 3)}-${currentValue.slice(3)}`;
    return `${currentValue.slice(0, 3)}-${currentValue.slice(
      3,
      5
    )}-${currentValue.slice(5, 9)}`;
  }
  return value;
};

// SSN validation function from CountryCitizenship
const validateSSNPattern = (ssn) => {
  if (!ssn) return false;
  const ssnPattern = /^\d{3}-\d{2}-\d{4}$/;
  return ssnPattern.test(ssn);
};

// Updated validation schema with better date error message
const validationSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  dateOfBirth: Yup.date()
    .typeError("Please enter a valid date of birth")
    .max(new Date(), "Date of birth cannot be in the future")
    .test("age", "You must be at least 18 years old", function (value) {
      if (!value) return false;
      const today = new Date();
      const birth = new Date(value);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birth.getDate())
      ) {
        age--;
      }
      return age >= 18;
    })
    .required("Date of birth is required"),
  country: Yup.string().required("Country of residence is required"),
  city: Yup.string().required("City is required"),
  street: Yup.string().required("street is required"),
  state: Yup.string().required("State is required"),
  zipCode: Yup.string()
    .required("ZIP code is required")
    .matches(
      /^\d{5}(?:-\d{4})?$/,
      "Enter a valid US ZIP code (12345 or 12345-6789)"
    ),
  ssn: Yup.string()
    .test(
      "ssn-format",
      "Please enter a valid SSN (XXX-XX-XXXX)",
      validateSSNPattern
    )
    .required("SSN is required"),
});

const countryOptions = [
  { value: "US", label: "United States" },
  // { value: "CA", label: "Canada" },
];

const PersonalDetails = () => {
  const theme = useTheme();
  const [states, setStates] = useState([]);
  const {
    navigateToSignupFundingMethod,
    navigateToSignupSurmountAccountPhoneVerification,
  } = useNavigation();

  const dispatch = useDispatch();

  // Load initial form data from localStorage
  const getInitialFormData = () => {
    try {
      const savedData = localStorage.getItem("personalDetails_formData");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        // Ensure dateOfBirth is properly converted to Date object
        if (parsed.dateOfBirth) {
          const date = new Date(parsed.dateOfBirth);
          // Check if the date is valid
          if (isNaN(date.getTime())) {
            parsed.dateOfBirth = null;
          } else {
            parsed.dateOfBirth = date;
          }
        }
        return parsed;
      }
    } catch (error) {
      console.error("Error loading form data from localStorage:", error);
    }
    return {
      firstName: "",
      lastName: "",
      dateOfBirth: null,
      country: "",
      city: "",
      street: "",
      state: "",
      zipCode: "",
      ssn: "",
    };
  };

  // TanStack Query mutation for submitting personal details
  const { mutate: submitPersonalDetails, isLoading: isSubmitting } =
    useMutation({
      mutationFn: combinedPersonalDetails,
      onSuccess: () => {
        // Navigate to phone verification on success
        navigateToSignupSurmountAccountPhoneVerification();
      },
    });

  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await getStates();
        const allStates = Object.entries(response).map(([key, value]) => ({
          value: value,
          label: key,
        }));
        setStates(allStates);
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };

    fetchStates();
  }, []);

  const formik = useFormik({
    initialValues: getInitialFormData(),
    validationSchema,
    onSubmit: async (values) => {
      // Save personal details to Redux state
      dispatch(
        setCreateAccountData({
          personalDetails: {
            firstName: values.firstName,
            lastName: values.lastName,
            dateOfBirth: values.dateOfBirth,
            country: values.country,
            city: values.city,
            street: values.street,
            state: values.state,
            zipCode: values.zipCode,
            ssn: values.ssn,
          },
        })
      );

      // Format date of birth to YYYY-MM-DD (using local timezone to avoid date shift)
      let formattedDateOfBirth = "";
      if (values.dateOfBirth) {
        const date = new Date(values.dateOfBirth);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        formattedDateOfBirth = `${year}-${month}-${day}`;
      }

      // Prepare payload according to API requirements
      const payload = {
        first_name: values.firstName,
        last_name: values.lastName,
        tax_id: values.ssn,
        country_of_tax_residence:
          values.country === "US" ? "USA" : values.country,
        date_of_birth: formattedDateOfBirth,
        street_address_1: values.street,
        city: values.city,
        state: values.state,
        postal_code: values.zipCode,
      };

      // Submit personal details via API
      submitPersonalDetails(payload);
    },
  });

  // Save formik values to localStorage when they change
  useEffect(() => {
    try {
      // Create a copy of the values and ensure dateOfBirth is properly serialized
      const valuesToSave = { ...formik.values };
      if (valuesToSave.dateOfBirth instanceof Date) {
        valuesToSave.dateOfBirth = valuesToSave.dateOfBirth.toISOString();
      }
      localStorage.setItem(
        "personalDetails_formData",
        JSON.stringify(valuesToSave)
      );
    } catch (error) {
      console.error("Error saving form data to localStorage:", error);
    }
  }, [formik.values]);

  const handleDateChange = (date) => {
    formik.setFieldValue("dateOfBirth", date);
  };

  const handleSSNChange = (e) => {
    const { value } = e.target;
    const formattedSSN = formatSSN(value, formik.values.ssn);
    formik.setFieldValue("ssn", formattedSSN);
  };

  const handleGoBack = () => {
    navigateToSignupFundingMethod();
  };

  return (
    <SignupFlowWrapperContainer>
      <SignupFlowContent>
        <div className="signup-flow-header">
          <h1>Let&apos;s start with the basics</h1>
          <p>
            Your data is secure. This information is collected on behalf of
            Quantbase, LLC, an SEC-registered investment adviser, to open and
            manage your account.
          </p>
        </div>

        <Divider />

        <PersonalDetailsContainer>
          <Form onSubmit={formik.handleSubmit}>
            <FormSection>
              <Text variant="cardHeading" as="h4" mb="8px">
                Personal details
              </Text>
              <Text variant="body" color="tertiary" mb="20px">
                Please provide your personal details, they will be used to
                complete your application for Surmount account.
              </Text>

              <FormRow>
                <Row>
                  <Col md={6}>
                    <StyledInput
                      type="text"
                      placeholder="First name"
                      isInvalid={
                        formik.touched.firstName && formik.errors.firstName
                      }
                      {...formik.getFieldProps("firstName")}
                    />
                    {formik.touched.firstName && formik.errors.firstName ? (
                      <div className="form-error">
                        {formik.errors.firstName}
                      </div>
                    ) : null}
                  </Col>
                  <Col md={6}>
                    <StyledInput
                      type="text"
                      placeholder="Last name"
                      isInvalid={
                        formik.touched.lastName && formik.errors.lastName
                      }
                      {...formik.getFieldProps("lastName")}
                    />
                    {formik.touched.lastName && formik.errors.lastName ? (
                      <div className="form-error">{formik.errors.lastName}</div>
                    ) : null}
                  </Col>
                </Row>
              </FormRow>

              <FormRow>
                <Row>
                  <Col md={12}>
                    <BTForm.Group className="input-form">
                      <DateTimePicker
                        id="dateOfBirth"
                        name="dateOfBirth"
                        portalId="personal_details_date_of_birth"
                        selected={formik.values.dateOfBirth}
                        placeholder="Date of birth"
                        maxDate={new Date()}
                        yearDropdownItemNumber={100}
                        scrollableYearDropdown
                        onChange={handleDateChange}
                        onBlur={() =>
                          formik.setFieldTouched("dateOfBirth", true)
                        }
                      />
                    </BTForm.Group>
                    {formik.touched.dateOfBirth && formik.errors.dateOfBirth ? (
                      <div className="form-error">
                        {formik.errors.dateOfBirth}
                      </div>
                    ) : null}
                  </Col>
                </Row>
              </FormRow>

              <FormRow>
                <Row>
                  <Col md={12}>
                    <BTForm.Group className="input-form">
                      <Select
                        options={countryOptions}
                        placeholder="Country of residence"
                        className="select-country-dd"
                        name="country"
                        value={
                          countryOptions.find(
                            (country) => country.value === formik.values.country
                          ) || null
                        }
                        onChange={(val) => {
                          if (val && val.value) {
                            formik.setFieldValue("country", val.value);
                          }
                        }}
                        onBlur={() => formik.setFieldTouched("country", true)}
                        styles={PersonalDetailsSelectStyle(theme)}
                      />
                    </BTForm.Group>
                    {formik.touched.country && formik.errors.country ? (
                      <div className="form-error">{formik.errors.country}</div>
                    ) : null}
                  </Col>
                </Row>
              </FormRow>

              <FormRow>
                <Row>
                  <Col md={3}>
                    <StyledInput
                      type="text"
                      placeholder="Street"
                      isInvalid={formik.touched.street && formik.errors.street}
                      {...formik.getFieldProps("street")}
                    />
                    {formik.touched.street && formik.errors.street ? (
                      <div className="form-error">{formik.errors.street}</div>
                    ) : null}
                  </Col>
                  <Col md={3}>
                    <StyledInput
                      type="text"
                      placeholder="City"
                      isInvalid={formik.touched.city && formik.errors.city}
                      {...formik.getFieldProps("city")}
                    />
                    {formik.touched.city && formik.errors.city ? (
                      <div className="form-error">{formik.errors.city}</div>
                    ) : null}
                  </Col>

                  <Col md={3}>
                    <BTForm.Group className="input-form">
                      <Select
                        options={states}
                        placeholder="State"
                        className="select-state"
                        name="state"
                        value={
                          states.find(
                            (state) => state.value === formik.values.state
                          ) || null
                        }
                        onChange={(val) => {
                          if (val && val.value) {
                            formik.setFieldValue("state", val.value);
                          }
                        }}
                        onBlur={() => formik.setFieldTouched("state", true)}
                        styles={PersonalDetailsSelectStyle(theme)}
                      />
                    </BTForm.Group>
                    {formik.touched.state && formik.errors.state ? (
                      <div className="form-error">{formik.errors.state}</div>
                    ) : null}
                  </Col>
                  <Col md={3}>
                    <StyledInput
                      type="text"
                      placeholder="ZIP code"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={10}
                      pattern="\\d{5}(-\\d{4})?"
                      title="Enter 5 digits or ZIP+4 (12345 or 12345-6789)"
                      isInvalid={
                        formik.touched.zipCode && formik.errors.zipCode
                      }
                      {...formik.getFieldProps("zipCode")}
                    />
                    {formik.touched.zipCode && formik.errors.zipCode ? (
                      <div className="form-error">{formik.errors.zipCode}</div>
                    ) : null}
                  </Col>
                </Row>
              </FormRow>
            </FormSection>

            <FormSection>
              <Text variant="cardHeading" as="h4">
                SSN number
              </Text>

              <FormRow>
                <Row>
                  <Col md={12}>
                    <StyledInput
                      type="text"
                      placeholder="Your SSN number"
                      isInvalid={formik.touched.ssn && formik.errors.ssn}
                      value={formik.values.ssn || ""}
                      onBlur={formik.handleBlur}
                      name="ssn"
                      onChange={handleSSNChange}
                      maxLength={11}
                    />
                    {formik.touched.ssn && formik.errors.ssn ? (
                      <div className="form-error">{formik.errors.ssn}</div>
                    ) : null}
                  </Col>
                </Row>
              </FormRow>
            </FormSection>
          </Form>
        </PersonalDetailsContainer>
      </SignupFlowContent>
      <SignupFlowFooter>
        <FooterRight>
          <Button variant="secondary" onClick={handleGoBack}>
            Go Back
          </Button>
          <Button
            variant="primary"
            onClick={formik.handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <Loader className="on-btn" /> : "Continue"}
          </Button>
        </FooterRight>
      </SignupFlowFooter>
    </SignupFlowWrapperContainer>
  );
};

export default PersonalDetails;
